import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getPlans, getSubscription } from '../services/billingApi';
import UserStreamManager from '../utils/UserStreamManager';
import { useAuth } from './AuthContext';

const BillingContext = createContext();

export function useBilling() {
  return useContext(BillingContext);
}

export function BillingProvider({ children }) {
  const { currentUser, userProfile } = useAuth();

  // Multi-subscription state (NEW)
  const [subscriptions, setSubscriptions] = useState([]);

  // Plans
  const [plans, setPlans] = useState([]);

  // Wallet collection
  const [wallets, setWallets] = useState([]);
  const [effectiveBalance, setEffectiveBalance] = useState(null);

  // Track if initial fetch has completed
  const [initialFetchComplete, setInitialFetchComplete] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);

  // Error state
  const [error, setError] = useState(null);

  // Fetch counter to handle race conditions
  const fetchCounterRef = useRef(0);

  // Derived subscription values
  const personalSubscription = useMemo(() =>
    subscriptions.find(s => s.type === 'personal'),
    [subscriptions]
  );

  const orgSubscription = useMemo(() =>
    subscriptions.find(s => s.type === 'organization'),
    [subscriptions]
  );

  // "has subscription" checks — includes all non-terminal statuses (ACTIVE, PAST_DUE, INCOMPLETE, TRIALING, UNPAID)
  // These are used for feature-gating: user paid for current period even if renewal payment failed
  const NON_TERMINAL_STATUSES = ['ACTIVE', 'PAST_DUE', 'INCOMPLETE', 'TRIALING', 'UNPAID'];
  const hasPersonalSubscription = NON_TERMINAL_STATUSES.includes(personalSubscription?.subscription?.status);
  const hasOrgSubscription = NON_TERMINAL_STATUSES.includes(orgSubscription?.subscription?.status);
  const hasAnyActiveSubscription = hasPersonalSubscription || hasOrgSubscription;

  // Strict "is active" checks — only ACTIVE status (for plan change eligibility)
  const isPersonalSubActive = personalSubscription?.subscription?.status === 'ACTIVE';
  const isOrgSubActive = orgSubscription?.subscription?.status === 'ACTIVE';

  // Fetch subscription status
  const fetchSubscription = useCallback(async () => {
    const currentFetchId = ++fetchCounterRef.current;
    if (!currentUser) {
      if (currentFetchId === fetchCounterRef.current) {
        setSubscriptions([]);
        setWallets([]);
        setEffectiveBalance(null);
        setLoading(false);
        setInitialFetchComplete(true);
      }
      return;
    }

    try {
      setLoading(true);
      const response = await getSubscription();

      if (currentFetchId !== fetchCounterRef.current) {
        return;
      }

      const data = response.data;

      // Update state with new multi-subscription response
      setSubscriptions(data.subscriptions || []);
      setWallets(data.wallets || []);
      setEffectiveBalance(data.effective_balance ?? 0);

      setError(null);
    } catch (err) {
      if (currentFetchId === fetchCounterRef.current) {
        console.error('[BillingContext] Failed to fetch subscription:', err);
        setError(err.response?.data?.detail || 'Failed to load subscription');
      }
    } finally {
      if (currentFetchId === fetchCounterRef.current) {
        setLoading(false);
        setInitialFetchComplete(true);
      }
    }
  }, [currentUser]);

  // Fetch available plans
  const fetchPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const response = await getPlans();
      setPlans(response.data.plans);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  // Refresh subscription data
  const refresh = useCallback(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Load data on mount and when user changes
  useEffect(() => {
    fetchSubscription();
    fetchPlans();
  }, [fetchSubscription, fetchPlans]);

  // A new account's billing request is refused until its email is verified (auth_middleware session gate), so fetch
  // again whenever the onboarding status moves (Oct 2 2026: the sidebar showed a grey bar until a page reload).
  const onboardingStatus = userProfile?.onboarding_status;
  const prevOnboardingStatus = useRef(onboardingStatus);
  useEffect(() => {
    if (prevOnboardingStatus.current === onboardingStatus) return;
    prevOnboardingStatus.current = onboardingStatus;
    if (currentUser && onboardingStatus) fetchSubscription();
  }, [onboardingStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to real-time wallet balance updates via WebSocket
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribeWallet = UserStreamManager.subscribe(
      'WALLET_BALANCE_UPDATE',
      (data) => {
        // Update specific wallet in collection by wallet_type
        setWallets(prevWallets => {
          const updatedWallets = prevWallets.map(w =>
            w.wallet_type === data.wallet_type
              ? {
                ...w,
                balance: data.balance,
                period_credits_used: data.period_credits_used,
                period_credits_granted: data.period_credits_granted,
              }
              : w
          );

          // Calculate effective balance locally (sum of all wallet balances)
          // This avoids the need for an API call since effective_balance = sum(wallet.balance)
          const newEffectiveBalance = updatedWallets.reduce((sum, w) => sum + (w.balance || 0), 0);
          setEffectiveBalance(newEffectiveBalance);

          return updatedWallets;
        });

        // NOTE: Removed fetchSubscription() call here to prevent excessive API calls
        // The effective balance is now calculated locally from wallet balances
      }
    );

    // Register for reconciliation on WebSocket reconnect
    const unsubscribeReconciliation = UserStreamManager.addReconciliationCallback((reason) => {
      fetchSubscription();
    });

    return () => {
      unsubscribeWallet();
      unsubscribeReconciliation();
    };
  }, [currentUser, fetchSubscription]);

  // Computed values — subscription status checks
  const isPastDue =
    personalSubscription?.subscription?.status === 'PAST_DUE' ||
    orgSubscription?.subscription?.status === 'PAST_DUE';

  const isIncomplete =
    personalSubscription?.subscription?.status === 'INCOMPLETE' ||
    orgSubscription?.subscription?.status === 'INCOMPLETE';

  const isUnpaid =
    personalSubscription?.subscription?.status === 'UNPAID' ||
    orgSubscription?.subscription?.status === 'UNPAID';

  // Non-terminal = subscription is alive in Stripe (user cannot create a new one)
  const hasNonTerminalSubscription =
    personalSubscription?.subscription?.status &&
    ['ACTIVE', 'PAST_DUE', 'INCOMPLETE', 'TRIALING', 'UNPAID'].includes(
      personalSubscription.subscription.status
    );

  const isCanceled =
    personalSubscription?.subscription?.cancel_at_period_end ||
    orgSubscription?.subscription?.cancel_at_period_end;

  // Scheduled downgrade detection
  const isScheduledDowngrade =
    personalSubscription?.subscription?.scheduled_change != null ||
    orgSubscription?.subscription?.scheduled_change != null;
  const personalScheduledChange = personalSubscription?.subscription?.scheduled_change || null;
  const orgScheduledChange = orgSubscription?.subscription?.scheduled_change || null;

  // Use effective balance for main credit balance (null means still loading)
  const creditBalance = effectiveBalance;

  // Extract specific wallet info from collection
  const subscriptionWallet = wallets.find(w => w.wallet_type === 'PERMANENT' || w.wallet_type === 'ORG_SUBSCRIPTION');

  // Display wallets: merge FREE_DAILY balance into Credits wallet for user-facing display.
  // Backend still returns FREE_DAILY as a separate wallet (keeps backend honest/1:1 with DB),
  // but users shouldn't see it since the free credits program is disabled (Apr 2026).
  // Raw `wallets` stays untouched for WebSocket updates and internal logic.
  const displayWallets = useMemo(() => {
    const freeDaily = wallets.find(w => w.wallet_type === 'FREE_DAILY');
    const freeDailyBalance = freeDaily?.balance || 0;

    return wallets
      .filter(w => w.wallet_type !== 'FREE_DAILY')
      .filter(w => {
        // Hide subscription wallets with no active sub AND no remaining credits
        // PERMANENT wallet is always shown (it's the unified personal wallet)
        if (w.wallet_type === 'ORG_SUBSCRIPTION' && !hasOrgSubscription && w.balance <= 0) return false;
        return true;
      })
      .map(w => {
        if (w.wallet_type === 'PERMANENT') {
          return { ...w, balance: w.balance + freeDailyBalance };
        }
        return w;
      });
  }, [wallets, hasPersonalSubscription, hasOrgSubscription]);

  // Balance checks using effective balance (only if we have data)
  const isNegativeBalance = effectiveBalance !== null && effectiveBalance < 0;
  const isLowBalance = !isNegativeBalance && subscriptionWallet && subscriptionWallet.balance < (subscriptionWallet.period_credits_granted * 0.2);
  const isCriticalBalance = !isNegativeBalance && subscriptionWallet && subscriptionWallet.balance < (subscriptionWallet.period_credits_granted * 0.1);

  const value = {
    // Multi-subscription state (NEW - primary values)
    subscriptions,
    personalSubscription,
    orgSubscription,
    hasPersonalSubscription,
    hasOrgSubscription,
    hasAnyActiveSubscription,
    isPersonalSubActive,
    isOrgSubActive,

    // Plans
    plans,

    // Wallets
    wallets,
    displayWallets,
    effectiveBalance,

    // Loading states
    loading,
    plansLoading,
    error,
    initialFetchComplete,

    // Computed
    isPastDue,
    isIncomplete,
    isUnpaid,
    hasNonTerminalSubscription,
    isCanceled,
    isScheduledDowngrade,
    personalScheduledChange,
    orgScheduledChange,
    creditBalance,
    isNegativeBalance,
    isLowBalance,
    isCriticalBalance,



    // Actions
    refresh,
    fetchPlans,
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
}
